// src/components/Portfolio/Portfolio.jsx
// Crypto portfolio tracker — CoinGecko, localStorage, chart, grid
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiTrendingUp, FiTrendingDown, FiPieChart, FiArrowUpRight, FiArrowDownRight, FiInfo, FiChevronDown, FiChevronUp, FiServer, FiHardDrive, FiGlobe, FiBox, FiLock, FiDownload, FiShield, FiAlertTriangle, FiActivity } from 'react-icons/fi';
import { useAuth } from '../../Context/AuthContext';
import { useAuthGate } from '../../Context/AuthGateContext';
import { apiFetch } from '../../services/apiClient';
import { PixelLoader } from '../../lib/pixelart';
import './Portfolio.css';

// ===========================
// STORAGE — solo para migrar portafolios viejos de localStorage al backend.
// El usuario logueado persiste en el backend; el invitado ve el demo (sin guardar).
// ===========================
const STORAGE_KEY = 'bitplify_portfolio';

const loadHoldings = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; } };

// ===========================
// DEMO (invitados sin sesión) — portafolio de ejemplo con precios en vivo
// ===========================
const DEMO_HOLDINGS = [
  { id: 'demo_btc', cgId: 'bitcoin',  symbol: 'BTC', name: 'Bitcoin',  logo: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',    amount: 0.42, buyPrice: 51200, date: '2024-02-18', platform: 'Bitplify', notes: 'Hold largo plazo' },
  { id: 'demo_eth', cgId: 'ethereum', symbol: 'ETH', name: 'Ethereum', logo: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png', amount: 5.1,  buyPrice: 2650,  date: '2024-05-09', platform: 'MetaMask', notes: 'Staking' },
  { id: 'demo_sol', cgId: 'solana',   symbol: 'SOL', name: 'Solana',   logo: 'https://assets.coingecko.com/coins/images/4128/large/solana.png',   amount: 78,   buyPrice: 98,    date: '2024-07-21', platform: 'Phantom',  notes: '' },
  { id: 'demo_usdt', cgId: 'tether',  symbol: 'USDT', name: 'Tether',  logo: 'https://assets.coingecko.com/coins/images/325/large/Tether.png',     amount: 1500, buyPrice: 1,     date: '2024-09-02', platform: 'Bitplify', notes: 'Listo para operar P2P' },
];

// ===========================
// COINGECKO
// ===========================
const CG = 'https://api.coingecko.com/api/v3';
const searchCryptos = async (q) => {
  if (!q || q.length < 2) return [];
  try { const r = await fetch(`${CG}/search?query=${encodeURIComponent(q)}`); const d = await r.json(); return (d.coins||[]).slice(0,8).map(c=>({id:c.id,symbol:c.symbol.toUpperCase(),name:c.name,logo:c.large||c.thumb})); } catch { return []; }
};

// Un solo /coins/markets nos da precio + cambio 24h + sparkline 7d por moneda.
// Reemplaza al viejo /simple/price y de paso alimenta los mini-gráficos por activo.
const fetchPrices = async (ids) => {
  if (!ids.length) return {};
  try {
    const r = await fetch(`${CG}/coins/markets?vs_currency=usd&ids=${ids.join(',')}&sparkline=true&price_change_percentage=24h`);
    if (!r.ok) throw new Error(r.status);
    const d = await r.json();
    const map = {};
    (d || []).forEach(c => {
      map[c.id] = {
        usd: c.current_price,
        usd_24h_change: c.price_change_percentage_24h ?? 0,
        sparkline: c.sparkline_in_7d?.price || [],
      };
    });
    return map;
  } catch { return {}; }
};

// Histórico real para la curva de rendimiento. Cacheado por (id, días) durante la
// sesión: el histórico apenas cambia y así no pegamos a la API rate-limited de CG
// cada vez que el usuario alterna timeframes. Devuelve [[ts, precioUSD], ...] o null.
const chartCache = new Map();
const fetchMarketChart = async (id, days) => {
  const key = `${id}:${days}`;
  if (chartCache.has(key)) return chartCache.get(key);
  try {
    const r = await fetch(`${CG}/coins/${id}/market_chart?vs_currency=usd&days=${days}`);
    if (!r.ok) throw new Error(r.status);
    const d = await r.json();
    const arr = Array.isArray(d.prices) ? d.prices : null;
    if (arr) chartCache.set(key, arr);
    return arr;
  } catch { return null; }
};

// Precio de una moneda en el instante t (forward-fill: último precio conocido ≤ t).
// Búsqueda binaria sobre la serie ordenada que devuelve CoinGecko.
const priceAt = (sortedPrices, t) => {
  if (!sortedPrices || !sortedPrices.length) return null;
  if (t <= sortedPrices[0][0]) return sortedPrices[0][1];
  if (t >= sortedPrices[sortedPrices.length - 1][0]) return sortedPrices[sortedPrices.length - 1][1];
  let lo = 0, hi = sortedPrices.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (sortedPrices[mid][0] <= t) lo = mid; else hi = mid - 1;
  }
  return sortedPrices[lo][1];
};

// ===========================
// HELPERS
// ===========================
const genId = () => `h_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
const fmt = (n, d=2) => { if(!n||isNaN(n)) return '$0'; if(Math.abs(n)>=1e6) return `$${(n/1e6).toFixed(1)}M`; return `$${n.toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d})}`; };
const fmtPct = (n) => `${n>=0?'+':''}${(n||0).toFixed(2)}%`;

const COLORS = { BTC:'#f7931a',ETH:'#627eea',BNB:'#f0b90b',SOL:'#9945ff',LTC:'#bfbbbb',TRX:'#ef0027',ADA:'#0033ad',XRP:'#23292f',DOGE:'#c2a633',AVAX:'#e84142',DOT:'#e6007a',MATIC:'#8247e5',LINK:'#2a5ada',UNI:'#ff007a',USDT:'#26a17b',USDC:'#2775ca' };
const getColor = (s) => COLORS[s] || '#16a34a';

// Platform categories with icons
const PLATFORM_TYPES = {
  'Bitplify': { type: 'Exchange', icon: FiServer, color: '#16a34a' },
  'Binance': { type: 'Exchange', icon: FiServer, color: '#f0b90b' },
  'Coinbase': { type: 'Exchange', icon: FiServer, color: '#0052ff' },
  'Kraken': { type: 'Exchange', icon: FiServer, color: '#5741d9' },
  'MetaMask': { type: 'Wallet', icon: FiHardDrive, color: '#f6851b' },
  'Phantom': { type: 'Wallet', icon: FiHardDrive, color: '#ab9ff2' },
  'Trust Wallet': { type: 'Wallet', icon: FiHardDrive, color: '#3375bb' },
  'Ledger': { type: 'Wallet', icon: FiHardDrive, color: '#000000' },
  'Uniswap': { type: 'DEX', icon: FiGlobe, color: '#ff007a' },
  'PancakeSwap': { type: 'DEX', icon: FiGlobe, color: '#d1884f' },
  'Raydium': { type: 'DEX', icon: FiGlobe, color: '#c200fb' },
  'Aave': { type: 'DeFi', icon: FiBox, color: '#b6509e' },
};

const getPlatformInfo = (name) => {
  if (!name) return { type: 'Otro', icon: FiBox, color: '#6b7280' };
  const found = PLATFORM_TYPES[name];
  if (found) return found;
  const lower = name.toLowerCase();
  if (lower.includes('wallet') || lower.includes('ledger') || lower.includes('trezor')) return { type: 'Wallet', icon: FiHardDrive, color: '#8b5cf6' };
  if (lower.includes('swap') || lower.includes('dex')) return { type: 'DEX', icon: FiGlobe, color: '#ec4899' };
  if (lower.includes('stake') || lower.includes('defi') || lower.includes('aave') || lower.includes('lido')) return { type: 'DeFi', icon: FiBox, color: '#f59e0b' };
  return { type: 'Exchange', icon: FiServer, color: '#3b82f6' };
};

// ===========================
// SVG DONUT
// ===========================
function DonutChart({ data }) {
  if (!data.length) return null;
  const total = data.reduce((s,d)=>s+d.value,0);
  if (total<=0) return null;
  let cum = 0;
  const size=120,cx=60,cy=60,r=48,sw=14;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="pf-donut">
      {data.map((d,i)=>{ const p=d.value/total; const da=2*Math.PI*r; const doff=da*(1-p); const rot=cum*360-90; cum+=p;
        return <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.color} strokeWidth={sw} strokeDasharray={da} strokeDashoffset={doff} transform={`rotate(${rot} ${cx} ${cy})`} className="pf-donut-seg" />;
      })}
      <text x={cx} y={cy-4} textAnchor="middle" className="pf-donut-total">{fmt(total,0)}</text>
      <text x={cx} y={cy+12} textAnchor="middle" className="pf-donut-label">Total</text>
    </svg>
  );
}

// ===========================
// PORTFOLIO CHART WITH TIMEFRAMES
// ===========================
// Rangos que la API pública de CoinGecko sirve con histórico REAL (hasta 365 días).
// Antes había 5A/Máx pero esos datos eran fabricados; mejor rangos honestos.
const TIMEFRAMES = [
  { key: '1W', label: '1S', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1Y', label: '1A', days: 365 },
];

const N_POINTS = 80;

function PortfolioChart({ holdings }) {
  const [tf, setTf]           = useState('1M');
  const [series, setSeries]   = useState(null);   // [{ ts, value }]
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(false);
  const [hoverIdx, setHover]  = useState(null);
  const areaRef = useRef(null);

  // Dependencia estable: solo cgId/cantidad/fecha afectan la curva histórica
  // (el precio en vivo cambia cada 30s y no debe re-disparar el fetch).
  const holdKey = holdings.map(h => `${h.cgId}:${h.amount}:${h.date}`).join('|');

  useEffect(() => {
    let cancel = false;
    setLoading(true); setErr(false); setHover(null);
    (async () => {
      if (!holdings.length) { setSeries(null); setLoading(false); return; }
      const tfObj = TIMEFRAMES.find(t => t.key === tf) || TIMEFRAMES[1];
      const ids = [...new Set(holdings.map(h => h.cgId))];
      const charts = await Promise.all(ids.map(id => fetchMarketChart(id, tfObj.days)));
      if (cancel) return;
      const byId = {};
      ids.forEach((id, i) => { byId[id] = charts[i]; });
      // Resiliente: con que al menos una moneda traiga histórico, dibujamos la curva.
      // Las que fallaron (rate-limit, sin histórico) caen a su precio actual/compra
      // plano, en vez de romper el gráfico entero. Solo error si fallan todas.
      const loadedIds = ids.filter(id => byId[id] && byId[id].length >= 2);
      if (loadedIds.length === 0) { setErr(true); setSeries(null); setLoading(false); return; }

      const now = Date.now();
      const oldestBuy    = Math.min(...holdings.map(h => new Date(h.date).getTime()));
      const earliestData = Math.max(...loadedIds.map(id => byId[id][0][0]));
      const start = Math.max(now - tfObj.days * 86400000, oldestBuy, earliestData);

      const pts = [];
      for (let i = 0; i <= N_POINTS; i++) {
        const t = start + (now - start) * (i / N_POINTS);
        let val = 0;
        for (const h of holdings) {
          if (t < new Date(h.date).getTime()) continue; // todavía no lo tenías
          const p = priceAt(byId[h.cgId], t) ?? h.cp ?? h.buyPrice; // fallback plano
          if (p != null) val += h.amount * p;
        }
        pts.push({ ts: t, value: val });
      }
      setSeries(pts); setLoading(false);
    })();
    return () => { cancel = true; };
  }, [tf, holdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const tfObj = TIMEFRAMES.find(t => t.key === tf) || TIMEFRAMES[1];

  const tabs = (
    <div className="pf-tf-tabs">
      {TIMEFRAMES.map(t => (
        <button key={t.key} className={`pf-tf-btn ${tf === t.key ? 'active' : ''}`} onClick={() => setTf(t.key)}>
          {t.label}
        </button>
      ))}
    </div>
  );

  if (!holdings.length) return null;

  // Estados de carga / sin histórico (honestos, sin inventar datos)
  if (loading || err || !series || series.length < 2) {
    return (
      <div className="pf-chart-wrap">
        <div className="pf-chart-header">
          <div className="pf-chart-left"><span className="pf-chart-title">Rendimiento</span></div>
          {tabs}
        </div>
        <div className={`pf-chart-state ${err ? 'is-err' : 'is-loading'}`}>
          {err ? 'Histórico no disponible para este rango ahora.' : 'Cargando histórico…'}
        </div>
      </div>
    );
  }

  const values   = series.map(p => p.value);
  const firstVal = values[0] || 0;

  // El header y el tooltip reflejan el punto bajo el cursor (estilo Delta/eToro);
  // sin cursor, muestran el período completo.
  const active   = hoverIdx != null ? series[hoverIdx] : series[series.length - 1];
  const refVal   = active.value;
  const changeAbs = refVal - firstVal;
  const changePct = firstVal > 0 ? (changeAbs / firstVal) * 100 : 0;
  const isUp  = changeAbs >= 0;
  const color = isUp ? '#16a34a' : '#ef4444';

  const min = Math.min(...values), max = Math.max(...values);
  const span = (max - min) || 1;
  const w = 500, h = 140, vpad = 6;
  const px = i => (i / (values.length - 1)) * w;
  const py = v => vpad + (1 - (v - min) / span) * (h - 2 * vpad);
  const linePts = values.map((v, i) => `${px(i)},${py(v)}`).join(' ');
  const areaPts = `0,${h} ${linePts} ${w},${h}`;

  // Posición del crosshair/tooltip en % del área (alineada con el viewBox, xpad=0)
  const hx = hoverIdx != null ? (hoverIdx / (values.length - 1)) * 100 : 0;
  const hy = hoverIdx != null ? (py(active.value) / h) * 100 : 0;

  const handleMove = (clientX) => {
    const el = areaRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setHover(Math.round(ratio * (series.length - 1)));
  };

  const fmtDate = (ts) => new Date(ts).toLocaleDateString('es', {
    day: 'numeric', month: 'short',
    ...(tfObj.days >= 365 ? { year: '2-digit' } : {}),
    ...(tfObj.days <= 7 ? { hour: '2-digit', minute: '2-digit' } : {}),
  });

  return (
    <div className="pf-chart-wrap">
      <div className="pf-chart-header">
        <div className="pf-chart-left">
          <span className="pf-chart-title">Rendimiento · {hoverIdx != null ? fmtDate(active.ts) : tfObj.label}</span>
          <div className="pf-chart-change-row">
            <span className={`pf-chart-change-val ${isUp ? 'up' : 'down'}`}>
              {isUp ? '+' : ''}{fmt(changeAbs)}
            </span>
            <span className={`pf-chart-badge ${isUp ? 'up' : 'down'}`}>
              {isUp ? <FiArrowUpRight /> : <FiArrowDownRight />}
              {fmtPct(changePct)}
            </span>
          </div>
        </div>
        {tabs}
      </div>

      <div
        className="pf-chart-area"
        ref={areaRef}
        onMouseMove={e => handleMove(e.clientX)}
        onMouseLeave={() => setHover(null)}
        onTouchStart={e => handleMove(e.touches[0].clientX)}
        onTouchMove={e => { handleMove(e.touches[0].clientX); }}
        onTouchEnd={() => setHover(null)}
      >
        <svg viewBox={`0 0 ${w} ${h}`} className="pf-chart-svg" preserveAspectRatio="none">
          <defs>
            <linearGradient id="pfChartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.18" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={areaPts} fill="url(#pfChartGrad)" />
          <polyline points={linePts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>

        {hoverIdx != null && (
          <>
            <div className="pf-chart-cross" style={{ left: `${hx}%` }} />
            <div className="pf-chart-dot" style={{ left: `${hx}%`, top: `${hy}%`, background: color }} />
            <div className={`pf-chart-tip ${hx > 72 ? 'flip' : ''} ${hx < 14 ? 'pin' : ''}`} style={{ left: `${hx}%` }}>
              <span className="pf-chart-tip-val">{fmt(active.value)}</span>
              <span className="pf-chart-tip-date">{fmtDate(active.ts)}</span>
            </div>
          </>
        )}
      </div>

      <div className="pf-chart-dates">
        <span>{fmtDate(series[0].ts)}</span>
        <span>{fmtDate(series[series.length - 1].ts)}</span>
      </div>
    </div>
  );
}

// ===========================
// ADD/EDIT MODAL
// ===========================
function HoldingModal({ holding, onSave, onClose }) {
  const [search,setSearch]=useState('');
  const [results,setResults]=useState([]);
  const [searching,setSearching]=useState(false);
  const [selected,setSelected]=useState(holding?{id:holding.cgId,symbol:holding.symbol,name:holding.name,logo:holding.logo}:null);
  const [amount,setAmount]=useState(holding?.amount?.toString()||'');
  const [buyPrice,setBuyPrice]=useState(holding?.buyPrice?.toString()||'');
  const [date,setDate]=useState(holding?.date||new Date().toISOString().slice(0,10));
  const [platform,setPlatform]=useState(holding?.platform||'');
  const [notes,setNotes]=useState(holding?.notes||'');
  const [error,setError]=useState('');
  const debRef=useRef(null);

  const handleSearch=(q)=>{setSearch(q);clearTimeout(debRef.current);if(q.length<2){setResults([]);return;}setSearching(true);debRef.current=setTimeout(async()=>{setResults(await searchCryptos(q));setSearching(false);},400);};
  const handleSave=()=>{if(!selected){setError('Selecciona una crypto');return;}if(!amount||parseFloat(amount)<=0){setError('Ingresa una cantidad válida');return;}if(!buyPrice||parseFloat(buyPrice)<=0){setError('Ingresa un precio de compra');return;}
    onSave({id:holding?.id||genId(),cgId:selected.id,symbol:selected.symbol,name:selected.name,logo:selected.logo,amount:parseFloat(amount),buyPrice:parseFloat(buyPrice),date,platform,notes});};

  return (
    <div className="pf-overlay" onClick={onClose}>
      <div className="pf-modal" onClick={e=>e.stopPropagation()}>
        <div className="pf-modal-head"><h3>{holding?'Editar holding':'Agregar crypto'}</h3><button className="pf-modal-x" onClick={onClose}><FiX /></button></div>
        <div className="pf-modal-body">
          {!selected?(
            <div className="pf-field"><label>Buscar crypto</label>
              <div className="pf-search-box"><FiSearch className="pf-search-ic" /><input className="pf-inp" value={search} onChange={e=>handleSearch(e.target.value)} placeholder="Bitcoin, Ethereum, Solana..." autoFocus /></div>
              {searching&&<span className="pf-hint">Buscando...</span>}
              {results.length>0&&(<div className="pf-results">{results.map(c=>(<button key={c.id} className="pf-result" onClick={()=>{setSelected(c);setSearch('');setResults([]);}}>
                <img src={c.logo} alt={c.symbol} className="pf-result-logo" /><span className="pf-result-name">{c.name}</span><span className="pf-result-sym">{c.symbol}</span></button>))}</div>)}
            </div>
          ):(
            <div className="pf-selected-chip"><img src={selected.logo} alt={selected.symbol} className="pf-chip-logo" /><div><strong>{selected.name}</strong><span>{selected.symbol}</span></div><button className="pf-chip-change" onClick={()=>setSelected(null)}>Cambiar</button></div>
          )}
          <div className="pf-row-2">
            <div className="pf-field"><label>Cantidad</label><input className="pf-inp" type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.5" step="any" /></div>
            <div className="pf-field"><label>Precio compra (USD)</label><div className="pf-prefix-wrap"><span className="pf-prefix">$</span><input className="pf-inp pf-inp-pre" type="number" value={buyPrice} onChange={e=>setBuyPrice(e.target.value)} placeholder="68000" step="any" /></div></div>
          </div>
          <div className="pf-row-2">
            <div className="pf-field"><label>Fecha</label><input className="pf-inp" type="date" value={date} onChange={e=>setDate(e.target.value)} /></div>
            <div className="pf-field">
              <label>Dónde la guardas</label>
              <input className="pf-inp" value={platform} onChange={e=>setPlatform(e.target.value)} placeholder="Ej: Binance, MetaMask, Ledger..." />
            </div>
          </div>
          <div className="pf-field"><label>Notas <span className="pf-opt">(opcional)</span></label><input className="pf-inp" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Hold, staking, DeFi..." /></div>
          {error&&<div className="pf-error"><FiInfo className="pf-error-ic" /> {error}</div>}
        </div>
        <div className="pf-modal-foot"><button className="pf-btn-cancel" onClick={onClose}>Cancelar</button><button className="pf-btn-save" onClick={handleSave}>{holding?'Guardar':'Agregar'}</button></div>
      </div>
    </div>
  );
}

// ===========================
// SPARKLINE 7d (por activo) — estilo Delta
// ===========================
function Sparkline({ data }) {
  if (!data || data.length < 2) return null;
  const up = data[data.length - 1] >= data[0];
  const min = Math.min(...data), max = Math.max(...data), span = (max - min) || 1;
  const w = 120, h = 28;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / span) * h}`).join(' ');
  return (
    <svg className="pf-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={up ? '#16a34a' : '#ef4444'} strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ===========================
// ISLA DINÁMICA (móvil) — píldora flotante con total + 24h al scrollear
// ===========================
function DynamicIsland({ visible, value, change, changePct, onClick }) {
  const up = change >= 0;
  return (
    <button className={`pf-island ${visible ? 'show' : ''} ${up ? 'up' : 'down'}`} onClick={onClick} tabIndex={visible ? 0 : -1} aria-hidden={!visible}>
      <span className="pf-island-dot" />
      <span className="pf-island-val">{fmt(value, 0)}</span>
      <span className="pf-island-chg">{up ? <FiArrowUpRight /> : <FiArrowDownRight />}{fmtPct(changePct)}</span>
    </button>
  );
}

// ===========================
// STABLECOINS — para el análisis "refugio vs riesgo" (misión educativa)
// ===========================
const STABLES = new Set(['USDT', 'USDC', 'DAI', 'BUSD', 'TUSD', 'USDP', 'FDUSD', 'USDD', 'PYUSD']);

// ===========================
// FILA DE INSIGHTS — data de un vistazo
// ===========================
function InsightsRow({ best, worst, topPos, topMover, totalValue }) {
  const cards = [];
  if (best && best.plPct > 0)
    cards.push({ label: 'Mejor activo', sym: best.symbol, logo: best.logo, val: fmtPct(best.plPct), cls: 'up', icon: <FiTrendingUp /> });
  if (worst && worst.plPct < 0 && worst.symbol !== best?.symbol)
    cards.push({ label: 'Peor activo', sym: worst.symbol, logo: worst.logo, val: fmtPct(worst.plPct), cls: 'down', icon: <FiTrendingDown /> });
  if (topPos && totalValue > 0)
    cards.push({ label: 'Mayor posición', sym: topPos.symbol, logo: topPos.logo, val: `${(topPos.value / totalValue * 100).toFixed(0)}%`, cls: 'neutral', icon: <FiPieChart /> });
  if (topMover && Math.abs(topMover.chg) > 0.01)
    cards.push({ label: 'Top mover 24h', sym: topMover.symbol, logo: topMover.logo, val: fmtPct(topMover.chg), cls: topMover.chg >= 0 ? 'up' : 'down', icon: <FiActivity /> });
  if (!cards.length) return null;
  return (
    <div className="pf-insights">
      {cards.map((c, i) => (
        <div className="pf-insight" key={i}>
          <span className="pf-insight-label">{c.icon} {c.label}</span>
          <div className="pf-insight-main">
            {c.logo && <img src={c.logo} alt="" className="pf-insight-logo" />}
            <strong className="pf-insight-sym">{c.sym}</strong>
            <span className={`pf-insight-val ${c.cls}`}>{c.val}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ===========================
// ANÁLISIS DE MEZCLA — estables (refugio) vs volátiles (riesgo) + concentración
// ===========================
function AllocationPanel({ enriched, totalValue }) {
  const live = enriched.filter(h => h.value > 0);
  if (totalValue <= 0 || live.length === 0) return null;
  const stableVal = live.filter(h => STABLES.has(h.symbol)).reduce((s, h) => s + h.value, 0);
  const volVal    = Math.max(totalValue - stableVal, 0);
  const stablePct = stableVal / totalValue * 100;
  const volPct    = 100 - stablePct;
  const top       = [...live].sort((a, b) => b.value - a.value)[0];
  const topPct    = top.value / totalValue * 100;
  const nAssets   = live.length;

  let note;
  if (topPct >= 60)
    note = { tone: 'warn', icon: <FiAlertTriangle />, text: `Alta concentración: ${topPct.toFixed(0)}% en ${top.symbol}. Diversificar en más activos reduce el riesgo si ${top.symbol} cae.` };
  else if (stablePct < 5)
    note = { tone: 'info', icon: <FiInfo />, text: `Casi todo en volátiles. Tener algo en estables (USDT/USDC) te da un refugio para cuando el mercado baja — y queda listo para operar P2P.` };
  else if (nAssets >= 4 && stablePct >= 10)
    note = { tone: 'good', icon: <FiShield />, text: `Buen balance: ${nAssets} activos y ${stablePct.toFixed(0)}% en estables como refugio.` };
  else
    note = { tone: 'info', icon: <FiInfo />, text: `${nAssets} ${nAssets === 1 ? 'activo' : 'activos'} · ${stablePct.toFixed(0)}% estable / ${volPct.toFixed(0)}% volátil. Las estables amortiguan las caídas del mercado.` };

  return (
    <div className="pf-alloc">
      <div className="pf-alloc-head"><FiShield className="pf-alloc-head-ic" /> Análisis de tu mezcla</div>
      <div className="pf-alloc-bar">
        {stablePct > 0 && <div className="pf-alloc-stable" style={{ width: `${stablePct}%` }} title={`Estables ${stablePct.toFixed(0)}%`} />}
        {volPct > 0    && <div className="pf-alloc-vol"    style={{ width: `${volPct}%` }}    title={`Volátiles ${volPct.toFixed(0)}%`} />}
      </div>
      <div className="pf-alloc-legend">
        <span className="pf-alloc-leg"><span className="pf-alloc-dot stable" /> Estables <strong>{stablePct.toFixed(0)}%</strong> · {fmt(stableVal, 0)}</span>
        <span className="pf-alloc-leg"><span className="pf-alloc-dot vol" /> Volátiles <strong>{volPct.toFixed(0)}%</strong> · {fmt(volVal, 0)}</span>
      </div>
      <div className={`pf-alloc-note ${note.tone}`}>{note.icon} {note.text}</div>
    </div>
  );
}

// Exporta el portafolio a CSV
function exportPortfolioCSV(rows) {
  const headers = ['Activo', 'Símbolo', 'Cantidad', 'Precio compra', 'Precio actual', 'Invertido', 'Valor actual', 'P&L', 'P&L %', 'Plataforma', 'Fecha'];
  const data = rows.map(h => [
    h.name, h.symbol, h.amount, h.buyPrice, h.cp?.toFixed(2) ?? '', h.invested?.toFixed(2) ?? '',
    h.value?.toFixed(2) ?? '', h.pl?.toFixed(2) ?? '', h.plPct?.toFixed(2) ?? '', h.platform || '', h.date,
  ]);
  const csv = [headers, ...data].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = `bitplify_portafolio_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click(); URL.revokeObjectURL(url);
}

// ===========================
// MAIN
// ===========================
export default function Portfolio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openAuth } = useAuthGate();
  const isGuest = !user;
  const requireReg = () => openAuth('register'); // invitado intenta construir → registro

  const [realHoldings,setHoldings]=useState([]);
  const holdings = isGuest ? DEMO_HOLDINGS : realHoldings; // invitado ve un portafolio de ejemplo
  const [loadingHoldings,setLoadingHoldings]=useState(!isGuest);
  const [prices,setPrices]=useState({});
  const [showModal,setShowModal]=useState(false);
  const [editing,setEditing]=useState(null);
  const [deleting,setDeleting]=useState(null);
  const [sortBy,setSortBy]=useState('value');
  const [sortDir,setSortDir]=useState('desc');

  // Carga del portafolio desde el backend (usuario logueado). Si el backend está
  // vacío pero hay holdings antiguos en localStorage, los migra una sola vez.
  useEffect(()=>{
    if(isGuest){ setLoadingHoldings(false); return; }
    let cancel=false;
    setLoadingHoldings(true);
    (async()=>{
      try{
        const res=await apiFetch('/api/portfolio');
        if(!res.ok) throw new Error('load');
        let data=await res.json();
        const local=loadHoldings();
        if((!data||data.length===0) && local.length>0){
          const imp=await apiFetch('/api/portfolio/import',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({holdings:local})});
          if(imp.ok){ data=await imp.json(); localStorage.removeItem(STORAGE_KEY); }
        }
        if(!cancel) setHoldings(Array.isArray(data)?data:[]);
      }catch{ /* backend caído: dejamos el portafolio vacío sin romper la vista */ }
      finally{ if(!cancel) setLoadingHoldings(false); }
    })();
    return()=>{cancel=true;};
  },[isGuest,user?.id]);

  const refreshPrices=useCallback(async()=>{
    const ids=[...new Set(holdings.map(h=>h.cgId))];
    if(ids.length) setPrices(await fetchPrices(ids));
  },[holdings]);

  useEffect(()=>{refreshPrices();const t=setInterval(refreshPrices,30000);return()=>clearInterval(t);},[refreshPrices]);

  // Alta/edición/baja: en invitado solo afectan el estado local; logueado persiste
  // en el backend y refleja la respuesta (id real / datos saneados).
  const handleAdd=async(h)=>{
    setShowModal(false);
    if(isGuest){ setHoldings(p=>[...p,h]); return; }
    try{
      const res=await apiFetch('/api/portfolio',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(h)});
      if(res.ok){ const saved=await res.json(); setHoldings(p=>[...p,saved]); }
    }catch{ /* sin red: no agregamos para no desincronizar con el backend */ }
  };
  const handleEdit=async(h)=>{
    setEditing(null);
    if(isGuest){ setHoldings(p=>p.map(x=>x.id===h.id?h:x)); return; }
    try{
      const res=await apiFetch(`/api/portfolio/${h.id}`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify(h)});
      if(res.ok){ const saved=await res.json(); setHoldings(p=>p.map(x=>x.id===saved.id?saved:x)); }
    }catch{ /* sin red: no editamos */ }
  };
  const handleDelete=async(id)=>{
    setDeleting(null);
    if(isGuest){ setHoldings(p=>p.filter(x=>x.id!==id)); return; }
    try{
      const res=await apiFetch(`/api/portfolio/${id}`,{method:'DELETE'});
      if(res.ok) setHoldings(p=>p.filter(x=>x.id!==id));
    }catch{ /* sin red: no eliminamos */ }
  };
  const toggleSort=(k)=>{if(sortBy===k) setSortDir(d=>d==='asc'?'desc':'asc');else{setSortBy(k);setSortDir('desc');}};

  // Enriched
  const enriched=holdings.map(h=>{const cp=prices[h.cgId]?.usd??0;const chg=prices[h.cgId]?.usd_24h_change??0;const v=h.amount*cp;const inv=h.amount*h.buyPrice;const pl=v-inv;const plP=inv>0?(pl/inv)*100:0;return{...h,cp,chg,value:v,invested:inv,pl,plPct:plP};});
  const sorted=[...enriched].sort((a,b)=>{const m=sortDir==='asc'?1:-1;if(sortBy==='value') return(a.value-b.value)*m;if(sortBy==='pl') return(a.plPct-b.plPct)*m;return a.symbol.localeCompare(b.symbol)*m;});

  const totalValue=enriched.reduce((s,h)=>s+h.value,0);
  const totalInvested=enriched.reduce((s,h)=>s+h.invested,0);
  const totalPL=totalValue-totalInvested;
  const totalPLPct=totalInvested>0?(totalPL/totalInvested)*100:0;
  const distribution=enriched.map(h=>({symbol:h.symbol,value:h.value,color:getColor(h.symbol)})).filter(d=>d.value>0).sort((a,b)=>b.value-a.value);
  const withValue=enriched.filter(h=>h.value>0);
  const best=withValue.length?withValue.reduce((a,b)=>a.plPct>b.plPct?a:b):null;
  const worst=withValue.length?withValue.reduce((a,b)=>a.plPct<b.plPct?a:b):null;
  const topPos=withValue.length?withValue.reduce((a,b)=>a.value>b.value?a:b):null;
  const topMover=withValue.length?withValue.reduce((a,b)=>Math.abs(a.chg)>Math.abs(b.chg)?a:b):null;

  // Cambio de "Hoy" (24h) del portafolio: valor 24h atrás = valor / (1 + chg%/100)
  const value24hAgo = enriched.reduce((s,h)=> s + (h.chg ? h.value/(1+h.chg/100) : h.value), 0);
  const today    = totalValue - value24hAgo;
  const todayPct = value24hAgo>0 ? (today/value24hAgo)*100 : 0;

  // Isla dinámica móvil: aparece cuando el resumen sale de la vista al scrollear.
  // Usamos un listener de scroll en captura (atrapa el scroll de cualquier
  // contenedor — window o panel interno) en vez de IntersectionObserver, que es
  // más robusto entre layouts y no depende de qué ancestro scrollea.
  const summaryRef = useRef(null);
  const [islandVisible, setIslandVisible] = useState(false);
  useEffect(()=>{
    if (holdings.length === 0) { setIslandVisible(false); return; }
    let raf = 0;
    const check = () => {
      raf = 0;
      const el = summaryRef.current;
      if (el) setIslandVisible(el.getBoundingClientRect().bottom < 8);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(check); };
    check();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return ()=>{ window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  },[holdings.length, isGuest]);

  return (
    <div className="pf-wrap">
      {holdings.length>0 && createPortal(
        <DynamicIsland
          visible={islandVisible}
          value={totalValue}
          change={today}
          changePct={todayPct}
          onClick={()=> summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        />,
        document.body
      )}
      <div className="pf-head">
        <div>
          <h1 className="pf-title">Mi Portafolio{isGuest && <span className="pf-demo-tag">DEMO</span>}</h1>
          <span className="pf-sub">{isGuest ? 'Datos de ejemplo · Precios en vivo' : `${holdings.length} ${holdings.length===1?'activo':'activos'} · Precios en vivo`}</span>
        </div>
        <button className="pf-add" onClick={()=> isGuest ? requireReg() : setShowModal(true)}><FiPlus /> Agregar</button>
      </div>

      {/* CTA de invitado — app abierta pero invitando a registrarse */}
      {isGuest && (
        <div className="pf-guest-cta">
          <div className="pf-guest-cta-main">
            <span className="pf-guest-cta-badge"><FiLock /> Vista de ejemplo</span>
            <h2 className="pf-guest-cta-title">Construye tu portafolio real</h2>
            <p className="pf-guest-cta-sub">Esto es un portafolio de ejemplo. Regístrate gratis para registrar tus inversiones de cualquier exchange o wallet y seguir tu rendimiento en tiempo real.</p>
            <div className="pf-guest-cta-btns">
              <button className="pf-cta-solid" onClick={()=>openAuth('register')}><FiPlus /> Crear mi portafolio gratis</button>
              <button className="pf-cta-ghost" onClick={()=>openAuth('login')}>Ya tengo cuenta</button>
            </div>
          </div>
        </div>
      )}

      {loadingHoldings ? (
        <div className="pf-loading"><PixelLoader px={5} label="Cargando tu portafolio…" /></div>
      ):holdings.length===0?(
        <div className="pf-empty">
          <FiPieChart className="pf-empty-ic" />
          <h3>Tu portafolio está vacío</h3>
          <p>Registra tus inversiones crypto de cualquier plataforma. Precios en tiempo real de 10,000+ monedas.</p>
          <button className="pf-empty-btn" onClick={()=> isGuest ? requireReg() : setShowModal(true)}><FiPlus /> Agregar primera crypto</button>
        </div>
      ):(
        <>
          {/* Summary + Donut */}
          <div className="pf-summary-row" ref={summaryRef}>
            <div className="pf-summary-left">
              <div className="pf-big-value">{fmt(totalValue,0)}</div>
              <div className="pf-change-pills">
                <div className={`pf-big-change ${totalPL>=0?'up':'down'}`}>
                  {totalPL>=0?<FiArrowUpRight />:<FiArrowDownRight />}
                  {fmt(Math.abs(totalPL))} ({fmtPct(totalPLPct)})
                  <span className="pf-change-tag">Total</span>
                </div>
                <div className={`pf-big-change pf-today ${today>=0?'up':'down'}`}>
                  {today>=0?<FiArrowUpRight />:<FiArrowDownRight />}
                  {fmt(Math.abs(today))} ({fmtPct(todayPct)})
                  <span className="pf-change-tag">Hoy</span>
                </div>
              </div>
              <div className="pf-summary-grid">
                <div className="pf-sg"><span className="pf-sg-label">Invertido</span><span className="pf-sg-val">{fmt(totalInvested,0)}</span></div>
                <div className="pf-sg"><span className="pf-sg-label">P&L</span><span className={`pf-sg-val ${totalPL>=0?'pf-green':'pf-red'}`}>{fmt(totalPL)}</span></div>
                {best&&best.plPct!==0&&(<div className="pf-sg"><span className="pf-sg-label">Top</span>
                  <div className="pf-sg-performer"><img src={best.logo} alt={best.symbol} className="pf-sg-logo" /><span className="pf-sg-val">{best.symbol}</span><span className={`pf-sg-badge ${best.plPct>=0?'up':'down'}`}>{fmtPct(best.plPct)}</span></div>
                </div>)}
              </div>
            </div>
            {distribution.length>1&&(
              <div className="pf-summary-right">
                <DonutChart data={distribution} />
                <div className="pf-legend">{distribution.map((d,i)=>(<span key={i} className="pf-legend-item"><span className="pf-legend-dot" style={{background:d.color}} />{d.symbol} {totalValue>0?`${(d.value/totalValue*100).toFixed(0)}%`:''}</span>))}</div>
              </div>
            )}
          </div>

          {/* Insights — data de un vistazo */}
          <InsightsRow best={best} worst={worst} topPos={topPos} topMover={topMover} totalValue={totalValue} />

          {/* Chart */}
          <PortfolioChart holdings={enriched} />

          {/* Análisis de mezcla — estables vs volátiles + concentración */}
          <AllocationPanel enriched={enriched} totalValue={totalValue} />

          {/* Sort + export */}
          <div className="pf-sort-bar">
            <span className="pf-sort-label">Ordenar:</span>
            {[{key:'value',label:'Valor'},{key:'pl',label:'Rendimiento'},{key:'name',label:'Nombre'}].map(s=>(
              <button key={s.key} className={`pf-sort-btn ${sortBy===s.key?'active':''}`} onClick={()=>toggleSort(s.key)}>
                {s.label}{sortBy===s.key&&(sortDir==='desc'?<FiChevronDown className="pf-sort-arrow" />:<FiChevronUp className="pf-sort-arrow" />)}
              </button>
            ))}
            {!isGuest && <button className="pf-export-btn" onClick={()=>exportPortfolioCSV(sorted)} title="Exportar a CSV"><FiDownload /> CSV</button>}
          </div>

          {/* Holdings — grid on desktop, list on mobile */}
          <div className="pf-grid">
            {sorted.map((h,idx)=>{
              const platInfo = getPlatformInfo(h.platform);
              const PlatIcon = platInfo.icon;
              return (
                <div key={h.id} className="pf-card" style={{'--c':getColor(h.symbol),animationDelay:`${idx*40}ms`}}>
                  <div className="pf-card-accent" />
                  <div className="pf-card-body">
                    {/* Platform badge — always visible, pulse to remind */}
                    <div className={`pf-plat ${h.platform ? 'pf-plat-set' : 'pf-plat-empty'}`} style={{'--pc': h.platform ? platInfo.color : '#6b7280'}}>
                      <PlatIcon className="pf-plat-ic" />
                      <span className="pf-plat-name">{h.platform || 'Sin ubicación'}</span>
                      <span className="pf-plat-type">{h.platform ? platInfo.type : '—'}</span>
                      {h.platform && <span className="pf-plat-pulse" style={{background: platInfo.color}} />}
                    </div>

                    {/* Top: logo + name + value */}
                    <div className="pf-card-top">
                      <img src={h.logo} alt={h.symbol} className="pf-card-logo" />
                      <div className="pf-card-info">
                        <span className="pf-card-sym">{h.symbol}</span>
                        <span className="pf-card-name">{h.name}</span>
                      </div>
                      <div className="pf-card-right">
                        <span className="pf-card-val">{fmt(h.value)}</span>
                        <span className={`pf-card-pl ${h.pl>=0?'up':'down'}`}>{h.pl>=0?'+':''}{fmt(h.pl)} ({fmtPct(h.plPct)})</span>
                        {totalValue>0 && h.value>0 && <span className="pf-card-alloc">{(h.value/totalValue*100).toFixed(0)}% cartera{STABLES.has(h.symbol) && <span className="pf-card-stable">estable</span>}</span>}
                      </div>
                    </div>

                    {/* Sparkline 7d */}
                    {prices[h.cgId]?.sparkline?.length > 1 && (
                      <div className="pf-spark-wrap"><Sparkline data={prices[h.cgId].sparkline} /><span className="pf-spark-tag">7d</span></div>
                    )}

                    {/* Stats */}
                    <div className="pf-card-grid">
                      <div className="pf-cg"><span>Cantidad</span><strong>{h.amount.toLocaleString('en-US',{maximumFractionDigits:8})}</strong></div>
                      <div className="pf-cg"><span>Precio actual</span><strong className={h.chg>=0?'pf-green':'pf-red'}>{fmt(h.cp)} <small>{fmtPct(h.chg)}</small></strong></div>
                      <div className="pf-cg"><span>Precio compra</span><strong>{fmt(h.buyPrice)}</strong></div>
                      <div className="pf-cg"><span>Invertido</span><strong>{fmt(h.invested)}</strong></div>
                    </div>

                    {/* Puente al marketplace: solo USDT (lo único que opera Bitplify P2P) */}
                    {h.symbol === 'USDT' && h.amount > 0 && (
                      <button
                        className="pf-sell-cta"
                        onClick={()=> isGuest ? requireReg() : navigate(`/create?type=sell&amount=${Math.floor(h.amount)}`)}
                        title="Publicá una oferta de venta de estos USDT en el marketplace"
                      >
                        <FiTrendingUp className="pf-sell-cta-ic" /> Vender en Bitplify <FiArrowUpRight />
                      </button>
                    )}

                    {/* Footer */}
                    <div className="pf-card-foot">
                      <span className="pf-date">{new Date(h.date).toLocaleDateString('es',{day:'numeric',month:'short',year:'2-digit'})}</span>
                      {h.notes&&<span className="pf-notes" title={h.notes}>{h.notes}</span>}
                      <div className="pf-card-acts">
                        <button className="pf-act" onClick={()=> isGuest ? requireReg() : setEditing(h)} title={isGuest ? 'Regístrate para editar' : 'Editar'}><FiEdit2 /></button>
                        <button className="pf-act pf-act-del" onClick={()=> isGuest ? requireReg() : setDeleting(h.id)} title={isGuest ? 'Regístrate para eliminar' : 'Eliminar'}><FiTrash2 /></button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {deleting&&(<div className="pf-overlay" onClick={()=>setDeleting(null)}><div className="pf-del-modal" onClick={e=>e.stopPropagation()}><p>¿Eliminar este holding?</p><div className="pf-del-btns"><button className="pf-btn-cancel" onClick={()=>setDeleting(null)}>Cancelar</button><button className="pf-btn-del" onClick={()=>handleDelete(deleting)}>Eliminar</button></div></div></div>)}
      {showModal&&<HoldingModal onSave={handleAdd} onClose={()=>setShowModal(false)} />}
      {editing&&<HoldingModal holding={editing} onSave={handleEdit} onClose={()=>setEditing(null)} />}
      {isGuest ? (
        <div className="pf-footer-cta">
          <span><FiLock className="pf-footer-ic" /> Estás viendo un ejemplo. Tu portafolio real se guarda al crear tu cuenta.</span>
          <button className="pf-cta-solid pf-cta-sm" onClick={()=>openAuth('register')}>Registrarme gratis</button>
        </div>
      ) : (
        <div className="pf-footer-info"><FiInfo className="pf-footer-ic" /> Precios cada 30s via CoinGecko · Datos en tu dispositivo</div>
      )}
    </div>
  );
}
